import type { SimpleSite } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  MaterialProductAutocomplete,
  type SchemaInput,
  TextField,
  TradeCompanySiteSelector,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Button, Stack } from 'ui';

import { useCreateSiteMaterial } from '../../hooks/useCreateSiteMaterial/useCreateSiteMaterial';

const schema = yup.object().shape({
  material_product: yup.number().required(),
  material_spec: yup.number().optional().nullable(),
  seller_label: yup.string().optional(),
  site: yup.number().required(),
});

type Props = {
  companyId: number;
};

export type FormValues = yup.InferType<typeof schema>;
export type FormInputValues = SchemaInput<typeof schema>;

export const NewCompanySiteMaterialForm = ({ companyId }: Props) => {
  const { t } = useTranslation('orders');
  const [siteMaterial, setSiteMaterial] = useState<SimpleSite | null>(null);
  const form = useForm<FormInputValues, unknown, FormValues>({
    resolver: yupResolver(schema),
  });
  const { mutate: createSiteMaterial, isPending } = useCreateSiteMaterial(form);

  const materialSpec = form.watch('material_spec');

  const onSubmit = (data: FormValues) => {
    createSiteMaterial({
      data: {
        material_product_id: data.material_product,
        seller_label: data.seller_label,
        trade_site_id: data.site,
      },
    });
  };

  return (
    <Form<FormInputValues, unknown, FormValues> {...form} onSubmit={onSubmit}>
      <TradeCompanySiteSelector
        name="site"
        label={t('siteMaterials.form.labels.site')}
        tradeCompanyId={companyId}
        onSelectOption={(site) => setSiteMaterial(site)}
        required
      />
      <AllowedMaterialSpecAutocomplete name="material_spec" label={t('siteMaterials.form.labels.materialSpec')} />
      <Stack
        direction="column"
        sx={{
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          variant="text"
          component="a"
          disabled={!siteMaterial}
          target="_blank"
          href={`/ground-control/material-products/create`}
          aria-label={t('siteMaterials.form.actions.createMaterialProductAriaLabel')}
        >
          {t('siteMaterials.form.actions.createMaterialProduct')}
        </Button>
        <MaterialProductAutocomplete
          name="material_product"
          label={t('siteMaterials.form.labels.materialProduct')}
          country={siteMaterial?.country}
          material_spec_id={materialSpec || undefined}
          required
          disabled={!siteMaterial}
        />
      </Stack>
      <TextField
        name="seller_label"
        label={t('siteMaterials.form.labels.sellerLabel')}
        infoTooltip={t('siteMaterials.form.tooltips.sellerLabel')}
      />
      <Button fullWidth type="submit" loading={isPending}>
        {t('siteMaterials.form.actions.create')}
      </Button>
    </Form>
  );
};
